import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { PhotoGalleryService } from '../instances/photo-gallery';
import { PhotoGalleryConverter } from '@/converters';

export const usePhotoGallery = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_PHOTO_GALLERY', params],
    async () => {
      const [err, data] = await PhotoGalleryService.getPhotoGalleries(params);
      if (err) {
        throw err;
      }
      return {
        data: PhotoGalleryConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePhotoGalleryById = (id: string) => {
  const query = useQuery(
    ['GET_PHOTO_GALLERY_BY_ID', id],
    async () => {
      const [err, data] = await PhotoGalleryService.getPhotoGalleryById(id);
      if (err) {
        throw err;
      }
      return PhotoGalleryConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostPhotoGalleryMutation = () => {
  return useMutation({
    mutationKey: ['POST_PHOTO_GALLERY'],
    mutationFn: async (payload: any) => {
      const [err, data] = await PhotoGalleryService.postPhotoGallery(payload);
      if (err) {
        throw err;
      }
      return PhotoGalleryConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutPhotoGalleryMutation = () => {
  return useMutation({
    mutationKey: ['PUT_PHOTO_GALLERY'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await PhotoGalleryService.putPhotoGallery(id, payload);
      if (err) {
        throw err;
      }
      return PhotoGalleryConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeletePhotoGalleryMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_PHOTO_GALLERY'],
    mutationFn: async (id: string) => {
      const [err] = await PhotoGalleryService.deletePhotoGallery(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useGetPhotoGalleryById = () => {
  return useMutation({
    mutationKey: ['GET_PHOTO_GALLERY_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await PhotoGalleryService.getPhotoGalleryById(uuid);
      if (err) {
        throw err;
      }
      return PhotoGalleryConverter.toDomainFromByIdResponse(data);
    },
  });
};
